import { useState, useEffect } from "react";
import { X, Plus } from "lucide-react";
import AppModal from "../common/AppModal";
import { editProduct } from "../../services";
import { ProductData } from "../../interface";
import { useAsync } from "../../hooks";

type Url = {
  id: number;
  url: string;
};

type EditProductFormProps = {
  onClose: () => void;
  isOpen: boolean;
  initialValue: any;
  OnEditProducts: (T: any) => void;
};

type FormErrors = {
  name?: string;
  sku?: string;
  clientPrice?: string;
  costPrice?: string;
  sourceUrl?: string;
  urls?: string[];
};

const URL_REGEX = /^(https?:\/\/)[\w\-]+(\.[\w\-]+)+[/#?]?.*$/;
const PRICE_REGEX = /^\d+(\.\d{1,2})?$/;

export const EditProductForm = ({
  onClose,
  isOpen,
  initialValue,
  OnEditProducts,
}: EditProductFormProps) => {
  const [urls, setUrls] = useState<{ id?: number; url: string }[]>([
    { url: "" },
  ]);
  const [removedUrlIds, setRemovedUrlIds] = useState<number[]>([]);
  const [form, setForm] = useState({
    name: "",
    sku: "",
    clientPrice: "",
    costPrice: "",
    sourceUrl: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const {
    loading: updateLoading,
    error: updateError,
    execute: updateExecute,
  } = useAsync<string, [number, Partial<ProductData>]>();

  useEffect(() => {
    if (initialValue && isOpen) {
      setRemovedUrlIds([]);
      setErrors({});
      setTouched({});
      setForm({
        name: initialValue.name ?? "",
        sku: initialValue.sku ?? "",
        clientPrice: initialValue.clientPrice?.toString() ?? "",
        costPrice: initialValue.costPrice?.toString() ?? "",
        sourceUrl: initialValue.ourSiteUrl ?? "",
      });
      setUrls(
        initialValue.urls?.length > 0
          ? initialValue.urls.map((u: Url) => ({ id: u.id, url: u.url }))
          : [{ url: "" }],
      );
    }
  }, [initialValue, isOpen]);

  const validate = (currentForm = form, currentUrls = urls): FormErrors => {
    const newErrors: FormErrors = {};

    if (!currentForm.name.trim()) newErrors.name = "Product name is required.";
    else if (currentForm.name.trim().length < 2)
      newErrors.name = "Name must be at least 2 characters.";

    if (!currentForm.sku.trim()) newErrors.sku = "SKU is required.";
    else if (currentForm.sku.trim().length < 2)
      newErrors.sku = "SKU must be at least 2 characters.";

    if (!currentForm.clientPrice)
      newErrors.clientPrice = "Client price is required.";
    else if (!PRICE_REGEX.test(currentForm.clientPrice))
      newErrors.clientPrice = "Enter a valid price (e.g. 10 or 10.99).";
    else if (parseFloat(currentForm.clientPrice) <= 0)
      newErrors.clientPrice = "Price must be greater than 0.";

    if (!currentForm.costPrice) newErrors.costPrice = "Cost price is required.";
    else if (!PRICE_REGEX.test(currentForm.costPrice))
      newErrors.costPrice = "Enter a valid price (e.g. 10 or 10.99).";
    else if (parseFloat(currentForm.costPrice) <= 0)
      newErrors.costPrice = "Price must be greater than 0.";

    if (!currentForm.sourceUrl.trim())
      newErrors.sourceUrl = "Source URL is required.";
    else if (!URL_REGEX.test(currentForm.sourceUrl.trim()))
      newErrors.sourceUrl =
        "Enter a valid URL starting with http:// or https://.";

    const urlErrors: string[] = currentUrls.map((u) => {
      if (!u.url.trim()) return "";
      if (!URL_REGEX.test(u.url.trim()))
        return "Enter a valid URL starting with http:// or https://.";
      return "";
    });

    if (urlErrors.some((e) => e !== "")) newErrors.urls = urlErrors;

    return newErrors;
  };

  const markAllTouched = () => {
    const allTouched: Record<string, boolean> = {
      name: true,
      sku: true,
      clientPrice: true,
      costPrice: true,
      sourceUrl: true,
    };
    urls.forEach((_, i) => (allTouched[`url_${i}`] = true));
    setTouched(allTouched);
  };

  const handleBlur = (field: string) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const handleFormChange = (field: keyof typeof form, value: string) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    if (touched[field]) setErrors(validate(updated, urls));
  };

  const addUrl = () => {
    const updated = [...urls, { url: "" }];
    setUrls(updated);
  };

  const removeUrl = (i: number) => {
    const removed = urls[i];
    if (removed.id) setRemovedUrlIds((prev) => [...prev, removed.id!]);
    const updated = urls.filter((_, idx) => idx !== i);
    setUrls(updated);
    setErrors(validate(form, updated));
  };

  const updateUrl = (i: number, val: string) => {
    const updated = urls.map((u, idx) => (idx === i ? { ...u, url: val } : u));
    setUrls(updated);
    if (touched[`url_${i}`]) setErrors(validate(form, updated));
  };

  const handleSubmit = async () => {
    markAllTouched();
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    const payload = {
      name: form.name,
      clientPrice: parseFloat(form.clientPrice),
      costPrice: parseFloat(form.costPrice),
      sku: form.sku,
      ourSiteUrl: form.sourceUrl,
      competitorsUrls: urls
        .filter((u) => u.url.trim() !== "")
        .map((u) => ({ ...(u.id && { id: u.id }), url: u.url })),
      ...(removedUrlIds.length > 0 && { removedUrlIds }),
    };

    const message = await updateExecute(editProduct, initialValue.id, payload);
    OnEditProducts({
      name: form.name,
      clientPrice: parseFloat(form.clientPrice),
      costPrice: parseFloat(form.costPrice),
      sku: form.sku,
      ourSiteUrl: form.sourceUrl,
      urls: urls
        .filter((u) => u.url.trim() !== "")
        .map((u) => ({ id: u.id, url: u.url })),
    });
    console.log("✅ Product updated:", message);
    onClose();
  };

  const inputStyle = (hasError: boolean) => ({
    display: "block",
    width: "100%",
    padding: "10px 14px",
    fontSize: "14px",
    border: `1px solid ${hasError ? "#f87171" : "#d1d5db"}`,
    borderRadius: "8px",
    outline: "none",
    boxSizing: "border-box" as const,
    backgroundColor: hasError ? "#fff7f7" : "white",
  });

  const labelStyle = {
    display: "block",
    fontSize: "14px",
    fontWeight: 500,
    marginBottom: "6px",
    color: "#374151",
  };

  const errorStyle = {
    fontSize: "12px",
    color: "#ef4444",
    marginTop: "4px",
  };

  return (
    <AppModal
      title="Edit Product"
      open={isOpen}
      handleClose={onClose}
      onSubmit={handleSubmit}
      action="Edit Product"
      color="#14b8a6"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {/* Product Name */}
        <div>
          <label style={labelStyle}>
            Product Name <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <input
            type="text"
            placeholder="Enter name"
            value={form.name}
            onChange={(e) => handleFormChange("name", e.target.value)}
            onBlur={() => handleBlur("name")}
            style={inputStyle(!!errors.name && touched.name)}
          />
          {touched.name && errors.name && (
            <p style={errorStyle}>{errors.name}</p>
          )}
        </div>

        {/* SKU */}
        <div>
          <label style={labelStyle}>
            SKU <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <input
            type="text"
            placeholder="Enter SKU"
            value={form.sku}
            onChange={(e) => handleFormChange("sku", e.target.value)}
            onBlur={() => handleBlur("sku")}
            style={inputStyle(!!errors.sku && touched.sku)}
          />
          {touched.sku && errors.sku && <p style={errorStyle}>{errors.sku}</p>}
        </div>

        {/* Client Price */}
        <div>
          <label style={labelStyle}>
            Client Price <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <input
            type="text"
            placeholder="Enter price"
            value={form.clientPrice}
            onChange={(e) => handleFormChange("clientPrice", e.target.value)}
            onBlur={() => handleBlur("clientPrice")}
            style={inputStyle(!!errors.clientPrice && touched.clientPrice)}
          />
          {touched.clientPrice && errors.clientPrice && (
            <p style={errorStyle}>{errors.clientPrice}</p>
          )}
        </div>

        {/* Cost Price */}
        <div>
          <label style={labelStyle}>
            Cost Price <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <input
            type="text"
            placeholder="Enter price"
            value={form.costPrice}
            onChange={(e) => handleFormChange("costPrice", e.target.value)}
            onBlur={() => handleBlur("costPrice")}
            style={inputStyle(!!errors.costPrice && touched.costPrice)}
          />
          {touched.costPrice && errors.costPrice && (
            <p style={errorStyle}>{errors.costPrice}</p>
          )}
        </div>

        {/* Source Product URL */}
        <div>
          <label style={labelStyle}>
            Source Product URL <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <input
            type="url"
            placeholder="Enter URL of product's source page"
            value={form.sourceUrl}
            onChange={(e) => handleFormChange("sourceUrl", e.target.value)}
            onBlur={() => handleBlur("sourceUrl")}
            style={inputStyle(!!errors.sourceUrl && touched.sourceUrl)}
          />
          {touched.sourceUrl && errors.sourceUrl && (
            <p style={errorStyle}>{errors.sourceUrl}</p>
          )}
        </div>

        {/* Dynamic URLs */}
        <div>
          <label style={labelStyle}>URL</label>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {urls.map((urlObj, i) => (
              <div key={urlObj.id ?? i}>
                <div
                  style={{ display: "flex", gap: "8px", alignItems: "center" }}
                >
                  <input
                    type="url"
                    placeholder={`URL ${i + 1}`}
                    value={urlObj.url}
                    onChange={(e) => updateUrl(i, e.target.value)}
                    onBlur={() => handleBlur(`url_${i}`)}
                    style={{
                      ...inputStyle(
                        !!(errors.urls?.[i] && touched[`url_${i}`]),
                      ),
                      flex: 1,
                    }}
                  />
                  <button
                    onClick={() => removeUrl(i)}
                    style={{
                      padding: "8px",
                      color: "#f87171",
                      border: "1px solid #fca5a5",
                      borderRadius: "8px",
                      background: "white",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
                {touched[`url_${i}`] && errors.urls?.[i] && (
                  <p style={errorStyle}>{errors.urls[i]}</p>
                )}
              </div>
            ))}
          </div>
          <button
            onClick={addUrl}
            style={{
              marginTop: "10px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "14px",
              color: "#0d9488",
              border: "1px solid #5eead4",
              borderRadius: "8px",
              background: "white",
              padding: "8px 14px",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            <Plus size={14} />
            Add Another URL
          </button>
        </div>
      </div>
    </AppModal>
  );
};
